import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Box, Image, keyframes, Skeleton, Text } from '@chakra-ui/react';

import { getDocumentationAsset } from 'api/documentation';

import { DOCUMENTATION_ASSET_PATTERN } from './utils';

type LoadAsset = (name: string) => Promise<string>;

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;

/** True while rendering a paragraph where an image sits within text. */
export const InlineImageContext = createContext(false);

const DocumentationAssetContext = createContext<LoadAsset | undefined>(
  undefined,
);

const readAsDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

/**
 * Documentation images are behind authentication, so they are fetched as
 * blobs and shown as data: URLs (the gateway CSP allows data: but not blob:
 * in img-src). Assets are content-addressed, so each name is fetched once per
 * visit; the cache is dropped when docs unmount.
 */
export const DocumentationAssetProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const cache = useRef(new Map<string, Promise<string>>());

  useEffect(() => {
    const assets = cache.current;
    return () => assets.clear();
  }, []);

  const load = useRef<LoadAsset>(name => {
    let url = cache.current.get(name);
    if (!url) {
      url = getDocumentationAsset(name).then(readAsDataUrl);
      url.catch(() => cache.current.delete(name));
      cache.current.set(name, url);
    }
    return url;
  });

  return (
    <DocumentationAssetContext.Provider value={load.current}>
      {children}
    </DocumentationAssetContext.Provider>
  );
};

export const DocumentationImage: React.FC<{ src?: string; alt?: string }> = ({
  src = '',
  alt = '',
}) => {
  const loadAsset = useContext(DocumentationAssetContext);
  const inline = useContext(InlineImageContext);
  const assetName = src.match(DOCUMENTATION_ASSET_PATTERN)?.[1];
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!assetName || !loadAsset) return;
    let active = true;
    setUrl(undefined);
    setFailed(false);
    loadAsset(assetName)
      .then(objectUrl => active && setUrl(objectUrl))
      .catch(() => active && setFailed(true));
    return () => {
      active = false;
    };
  }, [assetName, loadAsset]);

  if (inline) {
    // UI icons referenced in a sentence, sized to the text.
    return url ? (
      <Image
        src={url}
        alt={alt}
        display="inline-block"
        h="1.2em"
        w="auto"
        verticalAlign="text-bottom"
        mx={0.5}
      />
    ) : (
      <Box as="span" display="inline-block" minW="1.2em" h="1.2em">
        {failed ? alt : null}
      </Box>
    );
  }

  const frame = {
    as: 'span' as const,
    display: 'block',
    my: 4,
    maxW: 'full',
  };

  // Only packaged assets are rendered; anything else is never fetched.
  if (!assetName || failed) {
    return (
      <Box {...frame}>
        <Text as="span" fontSize="sm" color="gray.500" fontStyle="italic">
          Image unavailable{alt ? `: ${alt}` : ''}
        </Text>
      </Box>
    );
  }

  return (
    <Box {...frame}>
      {url ? (
        <Image
          src={url}
          alt={alt}
          maxW="full"
          maxH="560px"
          objectFit="contain"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="md"
          animation={`${fadeIn} 200ms ease-out`}
        />
      ) : (
        <Skeleton as="span" display="block" h="200px" borderRadius="md" />
      )}
    </Box>
  );
};
